import { MouseEventHandler } from "react";
import { addDays, subDays, format, parseISO } from "date-fns";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Button } from "@mui/material";
import { Box, Theme } from "@mui/system";
import { Typography } from "@mui/material";
import { useDateLocale } from "@/core/hooks/useDateLocale/useDateLocale";
import { dateFormats } from "@/core/data";
import { getTopLevelDomain } from "@/core/utils";
import { Domains } from "@/core/types/types";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";

const styles = (theme: Theme) => ({
  root: {
    position: "relative",
    height: "100%",
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.grey.A100}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minWidth: "160px",
    borderRadius: theme.spacing(0.5),
    "& button": {
      minWidth: theme.spacing(3),
    },
  },
});

interface DatePickerProps {
  value: string;
  isFullSize?: boolean;
  onPreviousDateSelect: (newDate: string) => void;
  onNextDateSelect: (newDate: string) => void;
  showWeekday?: boolean;
  futurePeriod?: number;
  disableFutureDates?: boolean;
}

const DatePicker = ({
  value,
  isFullSize,
  onPreviousDateSelect,
  onNextDateSelect,
  showWeekday,
  futurePeriod = -13,
  disableFutureDates = false,
}: DatePickerProps) => {
  const locale = useDateLocale();
  const classes: any = useCustomStyles(styles);
  const domain: Domains = getTopLevelDomain() as Domains;
  const dateFormat: string = dateFormats[domain];
  const FUTURE_LIMIT_DATE: Date = subDays(new Date(), futurePeriod);

  const nextDateIsDisabled: boolean =
    disableFutureDates &&
    new Date(value).getTime() > FUTURE_LIMIT_DATE.getTime();

  const handlePreviousDateSelect: MouseEventHandler<HTMLButtonElement> = () => {
    const newDate = subDays(parseISO(value), 1);
    onPreviousDateSelect(format(newDate, "yyyy-MM-dd"));
  };

  const handleNextDateSelect: MouseEventHandler<HTMLButtonElement> = () => {
    const newDate = addDays(parseISO(value), 1);
    onNextDateSelect(format(newDate, "yyyy-MM-dd"));
  };

  const formattedDate = (() => {
    if (showWeekday) {
      const extendedDateFormat: string = "eee, " + dateFormat;
      const date = format(parseISO(value), extendedDateFormat, { locale });
      // So., 01.10.2023 => So, 01.10.2023
      return date.replace(/\.,\s/, ", ");
    } else {
      return format(parseISO(value), dateFormat);
    }
  })();

  return (
    <Box
      sx={{
        width: isFullSize ? "100%" : "auto",
        maxHeight: 56,
      }}
    >
      <Box sx={classes?.root}>
        <Button
          type="button"
          size="small"
          color="secondary"
          onClick={handlePreviousDateSelect}
          sx={{
            height: isFullSize ? 56 : "100%",
          }}
          data-cy="prevDate"
        >
          <NavigateBeforeIcon fontSize="small" />
        </Button>

        <Typography sx={{ lineHeight: isFullSize ? 3 : 0 }}>
          {formattedDate}
        </Typography>

        <Button
          type="button"
          size="small"
          color="secondary"
          onClick={handleNextDateSelect}
          disabled={nextDateIsDisabled}
          sx={{
            height: isFullSize ? 50 : "100%",
          }}
          data-cy="nextDate"
        >
          <NavigateNextIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export { DatePicker };
